import { UploadIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Separator,
  Spinner,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { InfoItem } from "@/components/info-item";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import { AvatarTrackJourneyAssociationPopover } from "./avatar-track-journey-association-popover";
import { Order, useLazyLoadOrderInspectionQuery } from "@/shared/api/lod-api";
import { selectSelectedOrder } from "@/entities/annotation";
import { useAppSelector } from "@/app/appStore";
import { AnnotationStageDetailsDialog } from "@/features/annotation/ui/annotation-stage-statuses-dialog";
import { getAnnotationStatus } from "../lib/helpers";
import { ANNOTATION_STATUS } from "@/features/assignment/lib";

const getAnnotationStatusDisplay = (selectedOrder: Order | null) => {
  const status = getAnnotationStatus(selectedOrder);

  switch (status.type) {
    case ANNOTATION_STATUS.DONE:
      return <Badge color="green">{status.value}</Badge>;
    case ANNOTATION_STATUS.REJECTED:
    case ANNOTATION_STATUS.ANOMALY:
      return <Badge color="red">{status.value}</Badge>;
    case ANNOTATION_STATUS.NOT_STARTED:
    default:
      return status.value;
  }
};

export function GeneralInfoSection({
  isLoadingOrderInspection,
}: {
  isLoadingOrderInspection: boolean;
}) {
  const {
    data: gondolaChangeGroupsData,
    loadFromFile,
    loadFromEndpoint,
  } = useGondolaChangeGroupsData();
  const { orderId } = useParams<{
    orderId?: string;
  }>();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const [loadOrderInspection, { isLoading: isLoadingInspection }] =
    useLazyLoadOrderInspectionQuery();
  const [isStageDialogOpen, setIsStageDialogOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        loadFromFile(file);
      }

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [loadFromFile]
  );

  const handleFileImport = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleLoadInspection = useCallback(async () => {
    if (!orderId) return;

    try {
      const response = await loadOrderInspection({ orderId });
      if (response.data?.result) {
        loadFromEndpoint(response.data.result);
        toast.success("Order inspection data loaded successfully");
      } else {
        toast.error("Order inspection data is not available");
      }
    } catch (error) {
      console.error("Failed to load order inspection:", error);
      toast.error("Failed to load order inspection data");
    }
  }, [orderId, loadOrderInspection, loadFromEndpoint]);

  return (
    <Box>
      <Flex justify="between" align="center">
        <Text size="2" weight="bold" as="p" m="2">
          General info
        </Text>
        <Flex gap="2" align="center">
          <AvatarTrackJourneyAssociationPopover />
          <Tooltip
            content={
              !orderId
                ? "Order ID required to load inspection data"
                : "Load order inspection data"
            }
          >
            <IconButton
              onClick={handleLoadInspection}
              size="1"
              variant="ghost"
              color="gray"
              mr="2"
              disabled={!orderId || isLoadingInspection}
            >
              {isLoadingInspection ? <Spinner size="1" /> : <ReloadIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip content="Import gondola change groups from JSON file">
            <IconButton
              onClick={handleFileImport}
              size="1"
              variant="ghost"
              color="gray"
              mr="2"
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>
        </Flex>

        <input
          ref={inputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="min-h-0 h-0 max-h-0 invisible overflow-hidden w-0"
          style={{ display: "none" }}
        />
      </Flex>
      <Separator size="4" />
      {(isLoadingOrderInspection || isLoadingInspection) && (
        <Flex justify="center" align="center" m="2">
          <Spinner mr="2" size="2" />
          {isLoadingInspection
            ? "Loading order inspection data..."
            : "Loading latest order inspection data..."}
        </Flex>
      )}

      {gondolaChangeGroupsData ? (
        <Box px="2" py="1">
          <InfoItem label="Order type" value={selectedOrder?.order_source} />
          <InfoItem
            label="Order ID"
            value={gondolaChangeGroupsData?.order_id}
          />
          <InfoItem
            label="Order time"
            value={gondolaChangeGroupsData?.order_time}
          />
          <InfoItem
            label="Orphan proxs count"
            customValue={
              gondolaChangeGroupsData?.orphan_proximities?.length > 0 ? (
                <Badge color="red">
                  {gondolaChangeGroupsData?.orphan_proximities?.length}
                </Badge>
              ) : (
                `${gondolaChangeGroupsData?.orphan_proximities?.length}`
              )
            }
          />
          <InfoItem
            label="Orphan anns count"
            customValue={
              gondolaChangeGroupsData?.orphan_transaction_annotations?.length >
              0 ? (
                <Badge color="red">
                  {
                    gondolaChangeGroupsData?.orphan_transaction_annotations
                      ?.length
                  }
                </Badge>
              ) : (
                `${gondolaChangeGroupsData?.orphan_transaction_annotations?.length}`
              )
            }
          />
          <InfoItem
            label="Gondola changes groups count"
            value={gondolaChangeGroupsData?.gondola_change_groups?.length}
          />
          <InfoItem
            label="Journeys count"
            value={
              gondolaChangeGroupsData?.track_ids_per_journey_id
                ? Object.keys(gondolaChangeGroupsData.track_ids_per_journey_id)
                    .length
                : 0
            }
          />
          <InfoItem
            label="Annotation Status"
            customValue={getAnnotationStatusDisplay(selectedOrder)}
          />
          <Button
            variant="soft"
            color="gray"
            size="2"
            mt="2"
            className="w-full"
            onClick={() => setIsStageDialogOpen(true)}
          >
            Show Annotation Stage Details
          </Button>
        </Box>
      ) : (
        <Box height="128px" />
      )}
      <Separator size="4" />
      <AnnotationStageDetailsDialog
        order={selectedOrder}
        open={isStageDialogOpen}
        onOpenChange={() => setIsStageDialogOpen(false)}
      />
    </Box>
  );
}
